import { GlucoseSpecialValue } from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import { useMemo } from "react"
import { useQueryNewestReading } from "./useQueryNewestReading"
import { useFollowedUsers } from "src/contexts"
import { useDisplayGlucoseValue } from "src/hooks/glucoseFormatting"
import type { Reading } from "src/types"

export function useReadingDifference(reading: Reading): string | null {
  const { currentUserId } = useFollowedUsers()
  const displayGlucoseValue = useDisplayGlucoseValue()

  const beforeTime = reading.startedAt

  const afterTime = useMemo(
    () => dayjs(beforeTime).subtract(
      10,
      "minute",
    )
      .toISOString(),
    [beforeTime],
  )

  const previousReading = useQueryNewestReading({
    accountId: currentUserId ?? -1,
    afterTime,
    beforeTime,
  })

  return useMemo(
    () => {
      // if there is no prior reading then render nothing
      if (!previousReading) {
        return null
      }

      // if the current or previous value is a special value then render nothing
      if (
        reading.glucose.special_value === GlucoseSpecialValue.High ||
        reading.glucose.special_value === GlucoseSpecialValue.Low ||
        previousReading.glucose.special_value === GlucoseSpecialValue.High ||
        previousReading.glucose.special_value === GlucoseSpecialValue.Low
      ) {
        return null
      }

      // this only works because 0 mmol = 0 mgdl
      const recentValue = reading.glucose.mg_dl
      const secondValue = previousReading.glucose.mg_dl
      const diffValue = recentValue - secondValue
      const diffString = displayGlucoseValue(diffValue)

      if (diffValue > 0) {
        return `+${diffString}`
      }

      return `${diffString}`
    },
    [
      displayGlucoseValue,
      reading,
      previousReading,
    ],
  )
}
